import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as THREE from "three";
import {
  useRtfmAppState,
  type RtfmAppState,
} from "../hooks/useRtfmAppState.js";
import { StreamShell, StreamInfoDock, StreamInfoFooter, ErrorBanner, StatusBadge, TimerBadge } from "./StreamViewport.js";
import { twMerge } from "tailwind-merge";
import { DebugPhasePicker } from "./DebugPhasePicker.js";
import { DebugControllerToggle } from "./DebugControllerToggle.js";
import { ThemeToggle } from "./ThemeToggle.js";
import { ExternalLinkIcon, ResetIcon } from "@radix-ui/react-icons";
import { useTheme } from "../hooks/useTheme.js";
import { EndScreen } from "./EndScreen.js";
import { useSceneLoader } from "../hooks/useSceneLoader.js";
import { ControlsProvider, ControlsPanel } from "./controls/controls-provider.js";
import { RtfmDerivedState, useRtfmLifecycle } from "@/hooks/useRtfmLifecycle.js";
import { CarouselSection } from "./CarouselSection.js";

export interface RtfmUiHandles {
  viewportContainer: HTMLDivElement | null;
  controls: HTMLDivElement | null;
  status: HTMLDivElement | null;
  statusIndicator: HTMLSpanElement | null;
  statusText: HTMLSpanElement | null;
  stats: HTMLDivElement | null;
  statsFps: HTMLDivElement | null;
  statsResolution: HTMLDivElement | null;
  statsViewportMode: HTMLDivElement | null;
  errorBar: HTMLDivElement | null;
  errorLabel: HTMLSpanElement | null;
}

declare global {
  interface Window {
    __rtfmUi?: RtfmUiHandles;
  }
}

const getStatusMessage = (state: RtfmAppState, derived: RtfmDerivedState) => {
  if (state.error) {
    return "Error";
  }

  if (derived.isPost) {
    return "Session complete";
  }
  if (derived.isEnding) {
    return "Ending session";
  }
  if (derived.isPreparing) {
    return "Preparing stream";
  }
  if (derived.isQueueing) {
    return "In queue";
  }

  if (state.connectionStatus === "connected") {
    // if (state.streamState === "streaming") {
    //   return "Streaming";
    // }
    // if (state.streamState === "buffering") {
    //   return "Buffering stream…";
    // }
    return "Connected";
  }

  if (state.connectionStatus === "reconnecting") {
    return "Reconnecting…";
  }

  if (state.connectionStatus === "connecting") {
    return "Connecting…";
  }

  if (state.connectionStatus === "idle") {
    return "Idle";
  }

  return "Disconnected";
};

export const RtfmDemoApp = () => {
  const { state } = useRtfmAppState();
  const { derived, lifecycle } = useRtfmLifecycle();
  const sceneLoader = useSceneLoader();

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const controlsRef = useRef<HTMLDivElement | null>(null);
  const statusContainerRef = useRef<HTMLDivElement | null>(null);
  const statusIndicatorRef = useRef<HTMLSpanElement | null>(null);
  const statusTextRef = useRef<HTMLSpanElement | null>(null);
  const statsContainerRef = useRef<HTMLDivElement | null>(null);
  const statsFpsRef = useRef<HTMLDivElement | null>(null);
  const statsResolutionRef = useRef<HTMLDivElement | null>(null);
  const statsViewportRef = useRef<HTMLDivElement | null>(null);
  const errorBarRef = useRef<HTMLDivElement | null>(null);
  const errorLabelRef = useRef<HTMLSpanElement | null>(null);
  const [threeContext, setThreeContext] = useState<{
    renderer: THREE.WebGLRenderer;
    scene: THREE.Scene;
    camera: THREE.Camera;
  } | null>(null);

  const statusMessage = useMemo(
    () => getStatusMessage(state, derived),
    [state, derived],
  );

  const handleRequeue = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    window.__rtfmUi = {
      viewportContainer: viewportRef.current,
      controls: controlsRef.current,
      status: statusContainerRef.current,
      statusIndicator: statusIndicatorRef.current,
      statusText: statusTextRef.current,
      stats: statsContainerRef.current,
      statsFps: statsFpsRef.current,
      statsResolution: statsResolutionRef.current,
      statsViewportMode: statsViewportRef.current,
      errorBar: errorBarRef.current,
      errorLabel: errorLabelRef.current,
    };
    // Start non-React app once the UI handles (including #viewport-container) are present
    return () => {
      window.__rtfmUi = undefined;
    };
  }, []);

  useEffect(() => {
    if (!threeContext) {
      return;
    }

    if (window.appStarted === true) {
      return;
    }

    let cancelled = false;
    window.appStarted = true;
    import("../main.js")
      .then((m: any) => {
        if (cancelled) {
          return;
        }
        m.startRtfmApp({
          three: threeContext,
        });
      })
      .catch((e: any) => {
        console.error("Failed to start RTFM Demo:", e);
      });

    return () => {
      cancelled = true;
    };
  }, [threeContext]);

  const isLoading = state.sceneLoadStatus === "loading";
  const worldIsActive = derived.isActive && !isLoading;

  return (
    <ControlsProvider>
      <div className="relative w-[100dvw] h-[100dvh] bg-primary text-primary font-mono text-xs overflow-hidden touch-none">
        <Header />
        <Footer state={state} />
        <div className="relative flex flex-col min-h-[100dvh] max-h-[100dvh] w-[100dvw] items-center justify-center p-0 m-0 font-mono overflow-hidden">

          <main className={twMerge(
            "absolute flex flex-col gap-2 justify-center items-center inset-x-4 mx-auto p-4 max-w-full rounded-[2em] transition-[opacity,border] duration-500 ease-in-out",
            "min-w-xs max-w-sm sm:min-w-sm sm:max-w-sm sm:h-auto md:min-w-md md:max-w-md lg:min-w-lg lg:max-w-lg",
            derived.isActive && "border border-primary delay-[100ms]",
            !derived.isActive && "border border-transparent"
          )}
          >

            {/* div to keep the box height the same even as it transitions in and out */}
            <div className="relative w-full aspect-square flex flex-col justify-center items-center bg-transparent">
              <div
                className={twMerge(
                  "relative bg-transparent w-full transition-all duration-[1000ms] ease-in-out p-0 m-0 h-full border border-primary rounded-[1em]",
                  worldIsActive
                    ? "max-w-full max-h-full"
                    : "max-w-[3em] max-h-[3em]",
                )}
              >

                <div className={twMerge(
                  "absolute inset-0 flex items-center justify-center text-[1em] duration-200 ease-in-out rounded-[1em]",
                  derived.isActive && "pointer-events-none delay-[500ms] opacity-0",
                  isLoading && "opacity-100 delay-[100ms]",
                  !derived.isActive && "opacity-100 delay-[1000ms]"
                )}>
                  <EmojiWorlds />
                </div>

                <div className={twMerge(
                  "aspect-square p-0 m-0 transition-all ease-in-out flex flex-col justify-center items-center",
                  worldIsActive && "pointer-events-auto opacity-100 delay-[500ms] duration-[2000ms] hover:cursor-pointer blur-none", //shadow-xl shadow-primary 
                  !worldIsActive && "pointer-events-none opacity-0 shadow-none duration-500 ease-in-out " //blur-2xl
                )}>
                  <StreamShell
                    state={state}
                    derived={derived}
                    viewportRef={viewportRef}
                    controlsRef={controlsRef}
                    onCanvasReady={(context) => {
                      setThreeContext(context);
                    }}
                  />
                </div>
              </div>
            </div>

            <div className={twMerge(
              "w-full pointer-events-auto flex flex-col gap-2 duration-500 transition-[opacity,max-height] ease-in-out max-h-[100dvh]",
              derived.isActive && "opacity-100 pointer-events-auto delay-[500ms] max-h-[100dvh]",
              !derived.isActive && "opacity-0 pointer-events-none max-h-0"
            )}>
              <div className={"w-full flex flex-row gap-2 justify-between items-center select-none border border-transparent text-[0.8em] sm:text-[1em] rounded-[1em] py-1 px-2"}>
                <div className="w-1/3 flex flex-row justify-start items-center gap-2">
                  <StatusBadge
                    containerRef={statusContainerRef}
                    indicatorRef={statusIndicatorRef}
                    textRef={statusTextRef}
                    message={statusMessage}
                    status={state.connectionStatus}
                    timerVariant={derived.timerVariant}
                  />
                </div>
                <div className="w-1/3 grow flex flex-row justify-center items-center opacity-50 gap-1">
                  <LogoMark className="w-4 h-4 pb-1" />
                  <p className="w-min font-mono uppercase italic whitespace-nowrap text-center text-primary">
                    frameboy
                  </p>
                </div>
                <div className="w-1/3 flex flex-row justify-end items-center gap-2">
                  {derived.isActive && <TimerBadge
                    variant={derived.timerVariant}
                    label={derived.timerLabel}
                  />}
                </div>
              </div>
              <div className="w-full h-[8em]">
                <ControlsPanel sceneLoader={sceneLoader} />
              </div>
              <div className="w-full">
                <CarouselSection
                  state={state}
                  derived={derived}
                  lifecycle={lifecycle}
                  loadScene={sceneLoader.loadScene}
                />
              </div>
            </div>

          </main>

          <div className={twMerge(
            "fixed p-0 max-w-xs min-w-xs bottom-2 w-full h-auto flex flex-col gap-1 justify-end items-center duration-[500ms] ease-in-out transition-[opacity,border]",
            derived.isActive && "opacity-100 pointer-events-auto delay-[2000ms]",
            !derived.isActive && "opacity-0 pointer-events-none"
          )}>

            <StreamInfoFooter
              state={state}
            />

            <StreamInfoDock
              state={state}
              statsContainerRef={statsContainerRef}
              statsFpsRef={statsFpsRef}
              statsResolutionRef={statsResolutionRef}
              statsViewportRef={statsViewportRef}
            />

          </div>

          <div className="absolute max-w-sm min-w-sm w-full px-4 sm:px-0 flex flex-col top-2 items-start justify-center pointer-events-none z-50">
            <ErrorBanner
              state={state}
              containerRef={errorBarRef}
              labelRef={errorLabelRef}
            />
          </div>

          <div className={`absolute w-[24em] flex flex-col translate-y-1/2 mt-24 pointer-events-none ${(state.gatewayErrors > 1 && derived.isInitial) ? "opacity-100" : "opacity-0 hidden"}`}>
            <p className="bg-amber-400/10 text-amber-500 text-justify p-2 w-full">too many requests from your network, please try again later</p>
          </div>

          <div className="absolute max-w-sm min-w-sm w-full px-8 sm:px-0 flex flex-col translate-y-1/2 mt-36 items-start justify-center pointer-events-none">
            <QueuePanel
              state={state}
              derived={derived}
              onRequeue={derived.isPost ? handleRequeue : undefined}
            />
            <EndScreen
              state={state}
              derived={derived}
              didStartExperience={lifecycle.didStartExperience}
            />
          </div>
        </div>
      </div>
    </ControlsProvider>
  );
};

interface QueuePanelProps {
  state: RtfmAppState;
  derived: RtfmDerivedState;
  onRequeue?: () => void;
}

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

const QueuePanel = ({ state, derived, onRequeue }: QueuePanelProps) => {
  const clampedPosition = clamp01((state?.queuePosition ?? 0) / 10);
  const progressWidth = derived.isPost ? "0%" : `${Math.round((1 - clampedPosition) * 100)}%`;
  return (
    <div className={twMerge(
      "w-full flex flex-col justify-start items-center gap-4 uppercase text-primary select-none transition-all duration-500 ease-in-out opacity-90",
      (derived.isQueueing || derived.isPost) ? "translate-y-0 opacity-100 pointer-events-auto" : "translate-y-3 opacity-0 pointer-events-none",
    )}>
      <div className={"relative w-full flex flex-col gap-4 select-none"}>
        <div className={"flex items-center justify-between h-8 capitalize"} >
          <span>{derived.isPost ? "Play again?" : "Waiting in queue"}</span>
          {derived.isPost ? (
            <ButtonSimple className="w-min" onClick={onRequeue!}>
              <p>re-queue</p>
              <ResetIcon className="w-3 h-3" />
            </ButtonSimple>
          ) : (
            <span className="text-primary uppercase">{derived.queueLabel}</span>
          )}
        </div>
        <div className={`h-px w-full bg-dark-25 ${derived.isPost ? "opacity-0" : "opacity-100"} duration-200`}>
          <div
            className="h-full bg-dark transition-all duration-500 ease-out"
            style={{ width: progressWidth }}
          />
        </div>
      </div>
    </div>
  );
};

export function ButtonSimple({
  children,
  className,
  onClick,
  disabled,
  ...props
}: {
  children: React.ReactNode,
  className?: string,
  onClick?: React.MouseEventHandler<HTMLButtonElement>,
  disabled?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const classNames = twMerge(
    "flex items-center cursor-pointer justify-center gap-2 border border-primary focus:outline-none px-3 py-0.5 whitespace-nowrap text-primary opacity-100 hover:opacity-50 select-none",
    disabled ? "opacity-50 pointer-events-none cursor-default" : "opacity-100",
    className,
  );
  return (
    <button
      type="button"
      className={classNames}
      onClick={onClick}
      onMouseDown={(e) => {
        // Prevent default to avoid the button receiving focus on mouse click,
        // which would cause Spacebar to trigger another click while focused.
        props.onMouseDown?.(e);
        e.preventDefault();
      }}
      {...props}
      tabIndex={-1}
    >
      {children}
    </button>
  );
}

const EmojiWorlds = ({ enableBlinking = true, className }: { enableBlinking?: boolean, className?: string }) => {
  const emojiWorlds = ["🌍 ", "🌎 ", "🌏 "];
  // const emojiWorlds = ["🌒", "🌓", "🌔", "🌞", "🌖", "🌗", "🌘", "🌚"]; //"🌕", "🌑", 
  const emojiColors = ["#0000FF", "#00FF00", "#FF0000"]; //red green blue
  const [index, setIndex] = useState(0);
  const [blink, setBlink] = useState(true);

  useEffect(() => {
    if (!enableBlinking) {
      setBlink(true);
      return;
    }
    const interval = setInterval(() => {
      setBlink(!blink);

      if (!blink) {
        setIndex((index + 1) % emojiWorlds.length);
      }

    }, blink ? 400 : 150);
    return () => clearInterval(interval);
  }, [blink, index, enableBlinking]);

  return (
    <div className={twMerge(
      "flex flex-col gap-3 w-full h-full items-center justify-center bg-transparent select-none text-[2em] overflow-visible",
      className,
      enableBlinking && "animate-pulse"
      // blink ? "opacity-100" : "opacity-50",
      // shadow-small
    )}>
      {/* <div className="absolute inset-0 shadow-[0_0_24px_rgba(255,255,255,0.5)]" /> */}
      {/* {emojiWorlds[index]} */}
      <LogoMark className={"w-5 h-5 pl-0.25"} />
    </div>
  );
};

const Header = () => {
  return (
    <div className="fixed w-full top-0 flex flex-col text-primary/80 text-[.85em] md:text-[1.1em] tracking-tight text-xs touch-auto z-50">
      <div className="w-full px-4 py-2 flex flex-row justify-between items-center gap-2 whitespace-nowrap">
        <a href="https://worldlabs.ai" target="_blank" rel="noopener noreferrer">
          <div className="justify-start min-w-min border-none px-0" >
            <div className={"flex flex-row justify-start items-center gap-2 text-[1em] uppercase"}>
              <LogoMark className=" w-5 h-5 pb-1" />
              <p className="text-primary">World Labs</p>
            </div>
          </div>
        </a>

        <a href={import.meta.env.VITE_BLOG_POST_URL || "https://worldlabs.ai/blog/rtfm"} target="_blank" rel="noopener noreferrer">
          <div className="flex items-center gap-2 cursor-pointer opacity-100 hover:opacity-50">
            <h1 className="text-primary">What am I seeing?</h1>
            <ExternalLinkIcon />
          </div>
        </a>
      </div>
    </div>
  );
};

const Footer = ({ state }: { state: RtfmAppState }) => {
  const { lifecycle: { experiencePhase }, setDebugExperiencePhase } = useRtfmLifecycle();
  const isDebug =
    import.meta.env.MODE === "development" || process.env.DEV;
  return (
    <div className="flex text-xs fixed w-full bottom-0 flex-col uppercase select-none tracking-tight touch-auto z-50 hover:opacity-100 opacity-50" >
      <div className="w-full p-2.5 flex flex-row justify-between items-center gap-2">
        <div className="flex items-center gap-3 text-[9px] max-w-2/3 overflow-hidden py-0 h-4">
          {isDebug && (
            <>
              <DebugPhasePicker
                value={experiencePhase}
                onChange={(phase) => { setDebugExperiencePhase(phase); }}
              />
              <DebugControllerToggle />
              <div className="flex flex-row gap-4 p-1">
                <span>{`scene: ${state.sceneLoadStatus}`}</span>
                <span>{`scene_id: ${state.currentWorldId ? "success" : "failed"}`}</span>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-1 text-[11px]">
          <div className="md:ml-auto"><ThemeToggle /></div>
        </div>
      </div>

    </div>
  );
};

export const LogoMark = ({ className }: { className?: string }) => {
  const { theme } = useTheme();
  return (
    <div className={twMerge("flex items-center gap-2 w-6 h-6", className)}>
      <LogoMarkIcon fill={theme === "dark" ? "#ffffff" : "#000000"} className="w-full h-full" />
    </div>
  );
};

export const LogoMarkIcon = ({ fill, className }: { fill?: string, className?: string }) => {
  return (
    <svg width="34" height="30" viewBox="0 0 34 30" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path
        d="M0.0209966 1.19483L4.83324 24.43C5.04057 25.4301 6.18225 25.6437 6.8738 24.88L8.95119 22.8184C10.8294 20.9545 13.6148 21.1374 15.3266 23.1496L21.8834 29.7188C22.3635 30.2128 23.272 30.0205 23.5993 29.4183L33.854 11.524C34.315 10.7188 33.6166 9.96444 32.7041 10.2008L16.2214 15.9168C14.6841 16.5563 12.9177 17.2707 10.9195 15.0516L1.83241 0.395042C1.17632 -0.44213 -0.184969 0.158711 0.0209966 1.19483Z"
        fill={fill ?? "#FEFDFC"}
      />
    </svg>
  );
};
